//PDSubscriptionsIGifted.jsx
import React, { useCallback, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import { useSelector } from 'react-redux';
import Loader from '../../components/utils/Loader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift, faEye } from '@fortawesome/free-solid-svg-icons';

const PDSubscriptionsIGifted = () => {
    const authState = useSelector(state => state.authReducer);
    const gifterId = authState.user._id;
    const [fetchData, { loading }] = useFetch();

    const [iGifted, setIGifted] = useState([]);
    const [selectedIGiftedId, setSelectedIGiftedId] = useState('');

    //IGiftedList
    const fetchIGiftedList = useCallback(() => {
        const config = {
            url: `/pdgiftcoupon/gifter/${gifterId}`,
            method: "get",
            headers: { Authorization: authState.token }
        };

        fetchData(config, { showSuccessToast: false })
            .then(response => {
                if (response && Array.isArray(response.giftCoupons)) {
                    setIGifted(response.giftCoupons);
                }  else {
                    console.error("Invalid response format: expected array or object");
                }
            })
            .catch(error => {
                console.error("Error fetching gifted coupons:", error);
            });
        
    }, [authState.token, fetchData, gifterId]);

    useEffect(() => {
        fetchIGiftedList();
    }, [fetchIGiftedList]);

    const handleIGiftedDescView = (itemId) => {
        setSelectedIGiftedId(prevId => (prevId === itemId ? null : itemId));
    };

    const sortedIGifted = iGifted.sort((a, b) => {
        const dateA = new Date(a.dateCreated);
        const dateB = new Date(b.dateCreated);
        return dateB - dateA;
    });

    const formatDateTime = (date) => {
        const options = {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
            hour12: false
        };
        const jsDate = new Date(date);
        const day = jsDate.getDate().toString().padStart(2, '0');
        const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
        const year = jsDate.getFullYear();
        const hours = jsDate.getHours().toString().padStart(2, '0');
        const minutes = jsDate.getMinutes().toString().padStart(2, '0');
        const seconds = jsDate.getSeconds().toString().padStart(2, '0');
        return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
    };


    return (
        <div>
            <div className="rounded-xl bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 px-6 py-2 shadow-xl">
                <div className="flex items-center gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-white/20 backdrop-blur">
                        🎁
                    </div>

                    <div>
                        <h2 className="text-lg lg:text-xl font-bold text-white">
                            Gifts I Sent
                        </h2>

                        <p className="text-xs lg:text-sm text-purple-100 mt-1">
                            Track every surprise you've shared ✨
                        </p>
                    </div>
                </div>
            </div>
            <div className="w-full px-4 py-2">
                <div className="rounded-xl border border-white/10 bg-slate-900/70 backdrop-blur-xl px-5 py-2">
                
                    {loading ? (
                        <Loader />
                    ) : (
                        <div className='w-full'>
                            {iGifted && iGifted.length === 0 ? (
                                <div className="flex h-72 flex-col items-center justify-center gap-3">
                                    <div className="text-2xl lg:text-42xl">🎁</div>

                                    <h3 className="text-sm lg:text-lg font-semibold text-white">
                                        No Gifts Yet
                                    </h3>

                                    <p className="max-w-sm text-center text-xs lg:text-sm text-slate-400">
                                        The best gifts create unforgettable moments. Start by gifting a subscription to someone special.
                                    </p>
                                </div>
                            ) : (
                                sortedIGifted.map((igifted, index) => (
                                    <div
                                        key={igifted._id}
                                        className="
                                            w-full
                                            my-4
                                            rounded-3xl
                                            border
                                            border-purple-500/20
                                            bg-gradient-to-r
                                            from-slate-900
                                            via-purple-950/40
                                            to-blue-950/40
                                            px-5
                                            py-1
                                            shadow-xl
                                        "
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm lg:text-lg">🎁</span>

                                                    <span className="font-bold text-sm lg:text-lg text-white">
                                                        {igifted.product}
                                                    </span>
                                                </div>

                                                <div className="flex flex-wrap items-center gap-2 mt-2">
                                                    <span className="rounded-full bg-purple-500/20 px-3 py-1 text-xs text-purple-300">
                                                        🎁 PurpleDoor Gift
                                                    </span>
                                                    <span className="rounded-full bg-cyan-500/20 px-3 py-1 text-xs text-cyan-300">
                                                        👤 {igifted.recipientCode}
                                                    </span>
                                                    {igifted.redeemedAt ? (
                                                        <span className="rounded-full bg-green-500/20 px-3 py-1 text-xs text-green-300">
                                                            ✅ Redeemed
                                                        </span>
                                                    ) : (
                                                        <span className="rounded-full bg-yellow-500/20 px-3 py-1 text-xs text-yellow-300">
                                                            ⏳ Pending
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleIGiftedDescView(igifted._id)}
                                                className="rounded-xl w-10 border border-slate-700 bg-slate-800 px-1 py-1 text-purple-300 transition hover:border-purple-500 hover:bg-purple-500/10"
                                            >
                                                <FontAwesomeIcon icon={faEye} />
                                            </button>
                                        </div>
                                        <div className='text-gray-400'>
                                            <span className='text-xs lg:text-sm font-medium'>To: <span className='text-blue-400'>{igifted.recipientName}{" - "}{igifted.recipientCode}</span> on {formatDateTime(igifted.createdAt)}</span>
                                        </div>
                                        {selectedIGiftedId === igifted._id && (
                                            <div className="mt-4 rounded-xl border border-purple-500/20 bg-slate-950/70 p-5 backdrop-blur-sm">
                                                <div className="flex items-center justify-between mb-4">
                                                    <div>
                                                        <h3 className="text-xs lg:text-lg font-bold text-white flex items-center gap-2">
                                                            🎁 {igifted.product}
                                                        </h3>

                                                        <p className="text-xs text-slate-400 mt-1">
                                                            Gift Coupon Details
                                                        </p>
                                                    </div>

                                                    {igifted.redeemedAt ? (
                                                        <span className="rounded-full bg-green-500/20 px-3 py-1 text-xs font-semibold text-green-300">
                                                            ✅ Redeemed
                                                        </span>
                                                    ) : (
                                                        <span className="rounded-full bg-yellow-500/20 px-3 py-1 text-xs font-semibold text-yellow-300">
                                                            ⏳ Awaiting Redemption
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
                                                    <div className="rounded-xl bg-white/5 p-3">
                                                        <div className="text-[10px] uppercase tracking-wider text-slate-500">
                                                            Recipient
                                                        </div>

                                                        <div className="mt-1 font-semibold text-white">
                                                            👤 {igifted.recipientName}{" - "}{igifted.recipientCode}
                                                        </div>
                                                    </div>

                                                    <div className="rounded-xl bg-white/5 p-3">
                                                        <div className="text-[10px] uppercase tracking-wider text-slate-500">
                                                            Coupon Code
                                                        </div>

                                                        <div className="mt-1 font-mono text-purple-300 break-all">
                                                            🎟️ {igifted.couponCode}
                                                        </div>
                                                    </div>

                                                    <div className="rounded-xl bg-white/5 p-3">
                                                        <div className="text-[10px] uppercase tracking-wider text-slate-500">
                                                            Gift Value
                                                        </div>

                                                        <div className="mt-1 font-semibold text-cyan-300">
                                                            💰 ₹ {Number(igifted.giftValue).toFixed(2)}
                                                        </div>
                                                    </div>

                                                    <div className="rounded-xl bg-white/5 p-3">
                                                        <div className="text-[10px] uppercase tracking-wider text-slate-500">
                                                            Date Gifted
                                                        </div>

                                                        <div className="mt-1 font-medium text-white">
                                                            📅 {formatDateTime(igifted.createdAt)}
                                                        </div>
                                                    </div>

                                                </div>
                                                {igifted.giftMessage && (
                                                    <div className="mt-4 rounded-xl border border-yellow-500/20 bg-yellow-500/10 p-4">

                                                        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-yellow-300">
                                                            💌 Your Message
                                                        </div>

                                                        <div className="italic text-yellow-100 leading-relaxed">
                                                            "{igifted.giftMessage}"
                                                        </div>

                                                    </div>
                                                )}
                                                <div className="mt-4 rounded-xl bg-white/5 p-4">
                                                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
                                                        Redemption Status
                                                    </div>

                                                    {igifted.redeemedAt ? (
                                                        <div>
                                                            <div className="font-semibold text-green-300">
                                                                🎉 Successfully Redeemed
                                                            </div>

                                                            <div className="text-sm text-slate-300 mt-1">
                                                                Redeemed on {formatDateTime(igifted.redeemedAt)}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="font-semibold text-yellow-300">
                                                            ⏳ Waiting for recipient to redeem
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PDSubscriptionsIGifted;


